import type {
  APILanguage,
  APIVersion,
  RouteSegment,
  RouteDefinition,
  HttpVerb,
  ExtractSpecificVerbRoutes,
  ExtractGetRoutes,
  ExtractPostRoutes,
  ExtractPutRoutes,
  ExtractPatchRoutes,
  ExtractDeleteRoutes,
  Interceptor,
  InterceptorRequestData,
} from './types';

import RequestBuilder from './RequestBuilder';

type RequiredKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? never : K;
}[keyof T];

type HasRequiredKeys<T> = [RequiredKeys<T>] extends [never] ? false : true;

type DefaultRouteMethod<T> = {
  routeParams: T extends { routeParams?: infer R } ? R : never;
  headers: T extends { headers?: infer H } ? H : never;
  response: T extends { response: infer Resp } ? Resp : unknown;
  responseHeaders: T extends { responseHeaders: infer RespH } ? RespH : unknown;
  body: T extends { body: infer B } ? B : never;
} & (T extends { queryParams?: infer Q }
  ? HasRequiredKeys<Q> extends true
    ? { queryParams: Q }
    : { queryParams?: Q }
  : {});

const DEFAULT_ROOT_URL = 'https://api.openclassrooms.com';
const DEFAULT_VERSION: APIVersion = '0.1';
const DEFAULT_LANGUAGE: APILanguage = 'en';

export default class ClientApi<ApiRoutes extends RouteDefinition> {
  rootUrl: string;

  language: APILanguage;

  apiVersion: APIVersion;

  accessToken: string | null = null;

  requestInterceptors: Interceptor<InterceptorRequestData>[];

  constructor({
    rootUrl = DEFAULT_ROOT_URL,
    language = DEFAULT_LANGUAGE,
    version = DEFAULT_VERSION,
  }: {
    rootUrl?: string;
    language?: APILanguage;
    version?: APIVersion;
  }) {
    this.rootUrl = rootUrl;
    this.language = language;
    this.apiVersion = version;
    this.accessToken = null;
    this.requestInterceptors = [];

    this.addRequestInterceptor(this.injectApiVersion.bind(this)).addRequestInterceptor(
      this.checkAuthorizationHeader.bind(this)
    );
  }

  protected getFullUrl(path: RouteSegment): string {
    return this.rootUrl + path;
  }

  protected getAuthorizationHeader(headers: Headers): string | undefined {
    const authorization = headers.get('Authorization');

    // istanbul ignore next
    if (authorization) {
      return authorization;
    }

    return this.accessToken ? `Bearer ${this.accessToken}` : undefined;
  }

  protected checkAuthorizationHeader(requestData: InterceptorRequestData): InterceptorRequestData {
    const authorizationHeader = this.getAuthorizationHeader(requestData.options.headers);

    if (!authorizationHeader) {
      const error = `[OC API] try to call route ${requestData.url} without access token.`;
      throw new Error(error);
    }

    requestData.options.headers.set('Authorization', authorizationHeader);
    return requestData;
  }

  protected injectApiVersion(requestData: InterceptorRequestData): InterceptorRequestData {
    const actualAcceptHeader = requestData.options.headers.get('Accept');
    const acceptVersion = `version=${this.apiVersion}`;

    requestData.options.headers.set(
      'Accept',
      // istanbul ignore next
      actualAcceptHeader ? `${actualAcceptHeader};${acceptVersion}` : acceptVersion
    );

    return requestData;
  }

  public createRequest<
    Verb extends HttpVerb,
    Path extends ExtractSpecificVerbRoutes<Verb, ApiRoutes> & RouteSegment
  >(
    path: Path,
    method: Verb
  ): RequestBuilder<
    DefaultRouteMethod<ApiRoutes[Path] extends { [v in Verb]: infer M } ? M : never>
  > {
    const request = new RequestBuilder<
      DefaultRouteMethod<ApiRoutes[Path] extends { [v in Verb]: infer M } ? M : never>
    >(this.getFullUrl(path), method, this.language);

    this.requestInterceptors.forEach((interceptor) => {
      request.addRequestInterceptor(interceptor);
    });

    return request;
  }

  public createGetRequest<Path extends ExtractGetRoutes<ApiRoutes> & RouteSegment>(
    path: Path
  ): RequestBuilder<DefaultRouteMethod<ApiRoutes[Path] extends { GET: infer M } ? M : never>> {
    return this.createRequest(path, 'GET');
  }

  public createPostRequest<Path extends ExtractPostRoutes<ApiRoutes> & RouteSegment>(
    path: Path
  ): RequestBuilder<DefaultRouteMethod<ApiRoutes[Path] extends { POST: infer M } ? M : never>> {
    return this.createRequest(path, 'POST');
  }

  public createPatchRequest<Path extends ExtractPatchRoutes<ApiRoutes> & RouteSegment>(
    path: Path
  ): RequestBuilder<DefaultRouteMethod<ApiRoutes[Path] extends { PATCH: infer M } ? M : never>> {
    return this.createRequest(path, 'PATCH');
  }

  public createPutRequest<Path extends ExtractPutRoutes<ApiRoutes> & RouteSegment>(
    path: Path
  ): RequestBuilder<DefaultRouteMethod<ApiRoutes[Path] extends { PUT: infer M } ? M : never>> {
    return this.createRequest(path, 'PUT');
  }

  public createDeleteRequest<Path extends ExtractDeleteRoutes<ApiRoutes> & RouteSegment>(
    path: Path
  ): RequestBuilder<DefaultRouteMethod<ApiRoutes[Path] extends { DELETE: infer M } ? M : never>> {
    return this.createRequest(path, 'DELETE');
  }

  public addRequestInterceptor(
    interceptor: Interceptor<InterceptorRequestData>,
    prependInterceptor = false
  ): this {
    const addMethod = prependInterceptor ? 'unshift' : 'push';
    this.requestInterceptors[addMethod](interceptor);
    return this;
  }
}
